import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  ApiNetworkError,
  AuthController_signIn_v1,
  messageForApiError,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { type SessionEnding, useSession } from '@/auth/session';

import { authDescriptionId, AuthFrame, fieldLabel, fieldSurface } from './auth/AuthFrame';
import { PasswordInput } from './PasswordInput';
import { returnPathFrom } from './return-path';
import { BackToTill } from './till/BackToTill';

/**
 * Set into `AuthFrame` — the signage board that carries the design language for
 * every signed-out screen. The frame owns the identity, the datum rules and the
 * field lettering; this file owns the form and what it says.
 *
 * See `docs/design-language.md`. Do not restyle this screen locally: a change
 * that belongs to the language goes in the frame or the tokens, or the next
 * screen quietly disagrees with this one.
 */

/**
 * The back-office entry point: staff code and password, exchanged for a session.
 *
 * Three things about this screen are load-bearing rather than cosmetic.
 *
 * **It does not word a refusal.** `messageForApiError` does, from the code and
 * nothing else. `AUTH_CREDENTIALS_INVALID` covers an unknown staff code, a wrong
 * password and a deactivated user, and the API merges them deliberately — a
 * friendlier variant for any one of them hands anyone with a login form a roster
 * of who works at the park. `AUTH_ACCOUNT_LOCKED` is the one refusal that is
 * deliberately distinguishable, and its copy says the fifteen minutes and the
 * override without confirming that anyone is behind the staff code. Both
 * sentences live in FE003 so that eighteen screens cannot word them nineteen
 * ways.
 *
 * **Nothing here is logged.** The request body carries a password and the
 * response carries the only copy of the token there will ever be — it is stored
 * hashed server-side and cannot be re-read. Not in the catch, not behind a debug
 * flag, not at any level.
 *
 * **The staff code survives a failure.** Retyping it after a typo in the
 * password is a small cruelty repeated all day.
 */

/** Missing-input messages. These are this form's, not the API's. */
const REQUIRED_STAFF_CODE = 'Enter your staff code.';
const REQUIRED_PASSWORD = 'Enter your password.';

/**
 * What this screen will send, and what it refuses before sending.
 *
 * **No staff-code shape rule here, deliberately**, though `field-schemas.ts`
 * exports one and the field next door on the create-person form uses it. This is
 * the sign-in screen: refusing a malformed code locally would tell somebody
 * standing at the door that no such code could exist, which is a shape of the
 * enumeration the API closed. Anything non-empty goes to the server and comes
 * back as the one refusal that covers unknown code, wrong password and
 * deactivated user alike.
 *
 * The staff code is trimmed because a pasted trailing space is not a wrong code
 * and the API compares case-insensitively but not space-insensitively. The
 * password is never trimmed — a space in it is a character in it.
 */
const signInSchema = z.object({
  staffCode: z.string().trim().min(1, REQUIRED_STAFF_CODE),
  password: z.string().min(1, REQUIRED_PASSWORD),
});

/**
 * Not a refusal, so `messageForApiError` has nothing to say about it: nothing
 * came back at all. Worded as a connection to check rather than as a fault,
 * because on this screen it usually is one.
 */
const UNREACHABLE =
  'Could not reach the server. Check the network connection and try again — nothing was sent.';

/**
 * Neither a refusal nor the network: something in this client failed. It says so
 * rather than blaming the API, and it does not quote a code, because there is
 * none to quote.
 */
const UNEXPECTED =
  'Sign-in could not be completed. Try again, and report it if it keeps happening.';

/**
 * Why this person is looking at this screen.
 *
 * Most arrivals here are not first visits. A session lapses between two
 * keystrokes and the guard bounces whoever was working; somebody presses Sign
 * out; somebody types a URL they are not signed in for. All three used to land
 * on an identical blank form that explained none of it.
 */
type Arrival = SessionEnding | 'bounced' | 'fresh';

/**
 * One line, and only one. This screen's job is to get out of the way, so the
 * arrival line *replaces* the standing instruction rather than stacking on top
 * of it — a returning user does not read "use the staff code you were given"
 * for the four-hundredth time, and somebody who has just been thrown out of a
 * half-finished task needs the sentence underneath it instead.
 *
 * None of these says anything about a staff code, and that is deliberate. Every
 * one is derived from this browser's own history or its own navigation, never
 * from a server answer about a user, so none of them re-opens the enumeration
 * the API closed.
 */
const ARRIVAL_LINE: Record<Arrival, string> = {
  // Not "pick up where you were": that is only true when the guard recorded a
  // destination, and it is not this screen's to promise.
  expired: 'Your session ended. Sign in again to continue.',
  'signed-out': 'You are signed out.',
  // The path is not named. It is in the router's state and it is returned to
  // afterwards, but reading it back at somebody is noise on a door.
  bounced: 'Sign in to open that page.',
  fresh: 'Use the staff code and password you were given.',
};

export function SignIn() {
  const client = useApiClient();
  const { signedIn, endedBecause, acknowledgeEnding } = useSession();
  const location = useLocation();

  // `onTouched`, per docs/ui-conventions.md. The `Touched` map this screen used
  // to keep is gone: the rule it encoded — an empty field complains only once
  // somebody has typed in it — now lives in `FormField`, which is why the six
  // forms written after this one no longer have to rediscover it.
  const form = useForm({
    resolver: zodResolver(signInSchema),
    mode: 'onTouched',
    defaultValues: { staffCode: '', password: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);

  const pending = form.formState.isSubmitting;

  // Fixed at mount, so acknowledging the ending below does not change the
  // sentence out from under somebody who is mid-read.
  //
  // **This depends on the guard's redirect being a passive effect.** Freezing
  // the value trades away the self-correction the old recompute-every-render
  // version had: it is right only because react-router's `Navigate` fires from
  // a passive effect, so the navigation and `setEndedBecause` are scheduled in
  // the same flush and batched into the commit that mounts this screen. Move
  // that redirect into a layout effect, or replace it with an imperative
  // `navigate()` inside one, and an expired arrival mounts a commit early with
  // `endedBecause` still `null` and silently reads "Sign in to open that page."
  //
  // An ending outranks a bounce because it explains more: a lapsed session is
  // why the guard bounced them, not a second fact about the same trip.
  //
  // `returnPathFrom` rather than reading `state.from`: location state survives a
  // reload and comes back untyped, and that function is where this app already
  // decides whether such a value is one it may act on at all.
  const [arrival] = useState<Arrival>(
    () => endedBecause ?? (returnPathFrom(location.state) !== null ? 'bounced' : 'fresh'),
  );

  // The reason has now been shown, so it stops answering "why am I here" for
  // every later arrival at this screen. Without this it is tab-scoped rather
  // than arrival-scoped: sign out once and that line greets every subsequent
  // visit, including a bounce whose true sentence is a different one.
  useEffect(() => {
    if (endedBecause !== null) acknowledgeEnding();
  }, [acknowledgeEnding, endedBecause]);

  // A refusal takes the focus that disabling the submit button dropped on the
  // floor. Chrome focuses a button on mousedown, so `disabled` during the
  // request moves focus to `<body>`; re-enabled, the way back to the password
  // field would be a full tab cycle from the top of the document. The alert is
  // `role="alert"` and sits directly above the submit, so landing here both
  // announces the refusal and puts the fields one Shift+Tab away.
  const formErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (formError !== null) formErrorRef.current?.focus();
  }, [formError]);

  const handleSubmit = async ({ staffCode, password }: z.infer<typeof signInSchema>) => {
    // Belt and braces with the disabled button below. Double-submitting sign-in
    // burns two of the five attempts that lock the staff code.
    if (pending) return;

    setFormError(null);

    try {
      // The shape of both the body and the result comes from the generated
      // client. Nothing about `/v1/auth/sign-in` is written down in this file.
      const session = await client.request(AuthController_signIn_v1, {
        body: { staffCode, password },
      });

      // The token and the returned user go here and nowhere else. Where they
      // are kept is FE005's single decision, made behind this seam.
      //
      // And that is the whole of the success path: this screen does not decide
      // where a signed-in person goes. The route guard does, because it is the
      // thing that knows — the destination they were bounced from, and whether
      // a provisioned user must be sent to replace their credential before they
      // are sent anywhere else. A `navigate` here as well would be a second
      // opinion racing the first, and the two would disagree exactly for the
      // user whose credential is pending. The moment the session lands, the
      // guard on this route sees a signed-in viewer on a signed-out screen and
      // moves them on.
      signedIn(session);
    } catch (error) {
      // Deliberately silent. See the note above the component: neither the
      // credentials that went out nor the token that may have come back is
      // written anywhere, including here.
      if (error instanceof ApiError) {
        setFormError(messageForApiError(error));
      } else if (error instanceof ApiNetworkError) {
        setFormError(UNREACHABLE);
      } else {
        setFormError(UNEXPECTED);
      }

      // Neither field is cleared. The staff code especially: it was almost
      // certainly right, and retyping it to fix a password typo is the cruelty
      // this screen exists not to repeat.
    }
  };

  return (
    <AuthFrame
      title="Sign in"
      // The one element on this screen that varies. See ARRIVAL_LINE. The frame
      // sets it and gives it `authDescriptionId`; the staff-code field below
      // names that id, because it takes focus on mount and this sentence — the
      // whole point of the surface — would otherwise never be spoken to
      // somebody arriving by keyboard or screen reader.
      description={ARRIVAL_LINE[arrival]}
      // What the page never said: what to do when you cannot get in. All three
      // are checked against the code rather than written from memory — the
      // terminals line against `NOT_A_TILL` in `till/till-messages.ts`, the
      // credential line against `UsersController`, where every route that sets
      // somebody else's credential is `@RequireRole('OWNER')` and CL-104 is
      // explicit that a manager does not manage people.
      facts={
        <>
          <AuthFrame.Fact label="Estate">KFDC floriculture centre, Munnar</AuthFrame.Fact>
          <AuthFrame.Fact label="Terminals">
            Gate and outlet tills run the desktop app, not this page.
          </AuthFrame.Fact>
          <AuthFrame.Fact label="Lost credential">
            Only an owner can issue a new one. A manager cannot.
          </AuthFrame.Fact>
        </>
      }
    >
      {/* One column. A two-column form fails on a narrow window, and this one
          is two fields deep anyway. */}
      <Form {...form}>
        <form
          className="mt-10"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          {/* The two inputs are one group and share one rhythm. What follows
              them — the refusal, then the action — does not. */}
          <div className="space-y-5">
            <FormField
              control={form.control}
              name="staffCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Staff code
                  </FormLabel>
                  <FormControl
                    // The arrival line, named as this field's description
                    // because the field takes focus on mount: without it, the
                    // one sentence explaining why somebody is looking at this
                    // screen is never spoken to the person thrown out mid-task.
                    aria-describedby={authDescriptionId}
                    render={
                      <Input
                        // Codes are read and compared character by character, so
                        // they get the tabular figures every other code in this
                        // app gets.
                        //
                        // `font-mono` as well as `numeric`: the utility sets
                        // `font-variant-numeric` and nothing else. Elsewhere
                        // `numeric` sits on a `<code>`, which the user-agent
                        // sheet already gives a mono family — an `<input>` gets
                        // no such help, so without this the one value in the
                        // product that is read aloud and typed back in renders
                        // in the sans face.
                        className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                        // Every shift starts here and the first thing anybody
                        // does is click this field. Safe to take focus because
                        // the screen has exactly one job: the guard has already
                        // turned away anyone holding a session, so nothing else
                        // could have wanted the caret.
                        autoFocus
                        required
                        placeholder="ASHA01"
                        autoComplete="username"
                        autoCapitalize="characters"
                        spellCheck={false}
                        {...field}
                      />
                    }
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Password
                  </FormLabel>
                  <FormControl
                    render={
                      <PasswordInput
                        className={`${fieldSurface} h-11`}
                        required
                        // Not a hint at the shape of the value: there is no
                        // shape to hint at, and the API publishes no bound this
                        // screen could honestly restate. It says what to put
                        // here, which on a door is the useful sentence.
                        placeholder="Your password"
                        autoComplete="current-password"
                        {...field}
                      />
                    }
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Above the submit, and it stays there. A toast vanishes, and a staff
              member who looked away has no way to get it back — which for the
              lockout means losing the only explanation of why the next fifteen
              minutes will keep refusing them. */}
          {formError !== null && (
            // `tabIndex={-1}` makes it focusable programmatically without
            // putting it in the tab order. See `formErrorRef` above for why
            // focus lands here rather than being left wherever `disabled`
            // dropped it. The focus outline stays visible: this is the one
            // element that receives focus without being asked for.
            <Alert ref={formErrorRef} tabIndex={-1} variant="destructive" className="mt-5">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          {/* Separated from the field group, not another row of it. */}
          <Button
            type="submit"
            size="lg"
            className="mt-8 w-full"
            disabled={pending}
            aria-busy={pending}
          >
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>

          {/* On a terminal only (#259): whoever tapped the till's door to the
              back office and has no password here is not stranded in front of
              this prompt. */}
          <BackToTill type="button" variant="ghost" size="lg" className="mt-3 w-full">
            Back to the till
          </BackToTill>
        </form>
      </Form>
    </AuthFrame>
  );
}
